// Copyright (c) Microsoft. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BucketItem, BucketsState, BucketType, initialState } from './BucketsState';

export const bucketsSlice = createSlice({
    name: 'buckets',
    initialState,
    reducers: {
        setBuckets: (state: BucketsState, action: PayloadAction<BucketItem[]>) => {
            state.Buckets = action.payload;
        },
        updateBucket: (state: BucketsState, action: PayloadAction<BucketItem>) => {
            const bucketIndex = state.Buckets.findIndex((f) => f.id === action.payload.id);
            if (bucketIndex !== -1) {
                const bucket = state.Buckets[bucketIndex];
                state.Buckets[bucketIndex] = action.payload;

                // update child items if the parent bucket changed
                if (bucket.type === BucketType.Bucket) {
                    const updatedBuckets = state.Buckets.map((b) =>
                        b.parent?.id !== bucket.id ? b : { ...b, selected: action.payload.selected },
                    );
                    state.Buckets = updatedBuckets;
                }

                const parentBucket = state.Buckets.find((b) => b.id === bucket.parent?.id);

                if (parentBucket) {
                    if (parentBucket.selected && !action.payload.selected) {
                        parentBucket.selected = false;
                    }
                    const children = state.Buckets.filter((b) => b.parent?.id === parentBucket.id);
                    const selectedChildren = children.filter((b) => b.selected);
                    if (children.length === selectedChildren.length && !parentBucket.selected) {
                        parentBucket.selected = true;
                    }
                }
            }
        },
        addBucket: (state: BucketsState, action: PayloadAction<BucketItem>) => {
            const bucketExist = state.Buckets.find(
                (f) => f.id === action.payload.id || f.title === action.payload.title,
            );
            if (!bucketExist) {
                if (action.payload.type === BucketType.Bucket) {
                    // Remove bucket sources
                    const bucketSources = state.Buckets.filter((f) => f.parent?.id === action.payload.id);
                    if (bucketSources.length) {
                        state.Buckets = state.Buckets.filter((b) => !bucketSources.some((a) => a.id === b.id));
                    }
                }
                state.Buckets.push(action.payload);
            }
        },
        addBuckets: (state: BucketsState, action: PayloadAction<BucketItem[]>) => {
            state.Buckets = state.Buckets.concat(...action.payload);
        },
        removeBucket: (state: BucketsState, action: PayloadAction<BucketItem>) => {
            const bucketExist = state.Buckets.find((f) => f.id === action.payload.id);
            if (bucketExist) {
                state.Buckets = state.Buckets.filter(
                    (f) => f.id !== action.payload.id && f.id !== action.payload.parent?.id,
                );
            }
        },
        removeBuckets: (state: BucketsState, action: PayloadAction<BucketItem[]>) => {
            state.Buckets = state.Buckets.filter((b) => !action.payload.some((a) => a.id === b.id));
        },
    },
});

export const { setBuckets, addBucket, removeBucket, removeBuckets, addBuckets, updateBucket } = bucketsSlice.actions;

export default bucketsSlice.reducer;
